import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { dedupGet } from "@/lib/dedupGet";
import { Database, Table2, LayoutDashboard, Check, X, ArrowRight, Rocket } from "lucide-react";

export function OnboardingWizard({ onCreateDashboard }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const key = `bi_onboard_dismissed_${user?.id || "anon"}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(key) === "1");
  const [state, setState] = useState(null);

  useEffect(() => {
    if (dismissed) return;
    Promise.all([
      dedupGet("/connections").catch(() => ({ data: [] })),
      dedupGet("/datasets").catch(() => ({ data: [] })),
      dedupGet("/dashboards").catch(() => ({ data: [] })),
    ]).then(([c, ds, da]) => {
      const dashboards = da.data || [];
      setState({
        connected: (c.data || []).length > 0 || (ds.data || []).length > 0,
        dataset: (ds.data || []).length > 0,
        dashboard: dashboards.some((d) => (d.widgets || []).length > 0) || dashboards.length > 0,
      });
    });
  }, [dismissed]);

  if (dismissed || !state) return null;
  const steps = [
    { id: "connect", icon: Database, title: "Connect your data", desc: "Live database (Postgres, MySQL, Supabase, MongoDB) or upload a file.", done: state.connected, cta: "Connect", go: () => nav("/data") },
    { id: "dataset", icon: Table2, title: "Create a dataset", desc: "Pick a table, write SQL, or use the visual designer.", done: state.dataset, cta: "Create", go: () => nav("/data") },
    { id: "dashboard", icon: LayoutDashboard, title: "Build your first dashboard", desc: "Drag widgets onto the canvas and go live.", done: state.dashboard, cta: "Build", go: onCreateDashboard },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const nextIdx = steps.findIndex((s) => !s.done);

  const dismiss = () => { localStorage.setItem(key, "1"); setDismissed(true); };

  return (
    <div data-testid="onboarding-wizard" className="glass-card p-5 md:p-6 mb-6 relative overflow-hidden">
      <div className="absolute inset-0 hero-gradient opacity-40 pointer-events-none" />
      <button data-testid="onboarding-dismiss" onClick={dismiss} title="Dismiss"
        className="absolute top-3 right-3 z-10 p-1.5 rounded-full text-[color:var(--ink-3)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surface-2)] transition-colors">
        <X className="w-4 h-4" />
      </button>
      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          <Rocket className="w-5 h-5 text-[color:var(--brand-ocean)]" />
          <h2 className="font-display text-lg text-[color:var(--ink)]">Get started in 3 steps</h2>
          <span className="text-xs text-[color:var(--ink-3)] ml-1">{doneCount}/3 done</span>
        </div>
        <div className="h-1.5 rounded-full bg-[color:var(--surface-2)] max-w-[220px] mb-5 overflow-hidden">
          <div className="h-full rounded-full bg-[color:var(--brand-mint)] transition-all duration-500" style={{ width: `${(doneCount / 3) * 100}%` }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {steps.map((s, i) => (
            <div key={s.id} data-testid={`onboarding-step-${s.id}`}
              className={`rounded-xl border p-4 bg-[color:var(--surface)]/70 transition-all ${i === nextIdx ? "border-[color:var(--brand-ocean)] shadow-[var(--shadow-md)]" : "border-[color:var(--glass-border)]"} ${s.done ? "opacity-70" : ""}`}>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.done ? "bg-[color:var(--brand-mint)] text-white" : "bg-[color:var(--surface-2)] text-[color:var(--brand-ocean)]"}`}>
                  {s.done ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                </div>
                <span className="text-sm font-medium text-[color:var(--ink)]">{i + 1}. {s.title}</span>
              </div>
              <p className="text-xs text-[color:var(--ink-3)] mt-2 min-h-[32px]">{s.desc}</p>
              {!s.done && (
                <Button data-testid={`onboarding-cta-${s.id}`} size="sm" variant={i === nextIdx ? "default" : "outline"} onClick={s.go}
                  className={`mt-2 h-8 ${i === nextIdx ? "bg-[color:var(--brand-ocean)] text-white hover:opacity-90" : ""}`}>
                  {s.cta}<ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
